/**
 * Highlight the leading update label in the home page alert.
 *
 * The shared home renderer sets `#pageAlert` from the CMS `PageAlert` HTML
 * (via innerHTML). When that alert opens with an update label — e.g.
 * "JULY 2026 UPDATE:" — this site-scoped helper isolates just that leading
 * label and wraps it in a dark-red `span` so the timing stands out. The rest
 * of the alert is untouched, and the CMS content is not modified.
 *
 * The wrap is built with DOM APIs (splitText + createElement + textContent),
 * not innerHTML, so it is not flagged as an HTML-injection sink.
 *
 * @module pageAlert
 */

const LABEL_CLASS = "pagealert-update-label";

/**
 * Match the leading run of the alert text up to and including the first
 * "UPDATE:", bounded to near the start so a stray "update:" deeper in the body
 * cannot color a large span. Case-insensitive on the keyword; the original
 * casing is preserved in the output.
 * @type {RegExp}
 */
const UPDATE_LABEL_RE = /^\s*(.{0,40}?UPDATE:)/i;

/**
 * Wrap the leading "…UPDATE:" label of `#pageAlert` in a dark-red span.
 * Idempotent and a safe no-op when the alert is absent or does not start with
 * an update label.
 *
 * @returns {void}
 */
export function highlightPageAlertUpdate() {
    const alert = document.getElementById("pageAlert");
    if (!alert || alert.querySelector(`.${LABEL_CLASS}`)) {
        return;
    }

    const match = UPDATE_LABEL_RE.exec(alert.textContent);
    if (!match) {
        return;
    }
    const label = match[1].trim();

    const textNode = firstLabelTextNode(alert, label);
    if (!textNode) {
        return;
    }
    wrapLabel(textNode, textNode.textContent.indexOf(label), label);
}

/**
 * Return the first text node in `alert` that contains `label` as a run, or
 * null if the label is split across nodes (in which case highlighting is
 * skipped rather than guessed at).
 *
 * @param {Element} alert - The `#pageAlert` element.
 * @param {string} label - The update label to locate.
 * @returns {Text|null}
 */
function firstLabelTextNode(alert, label) {
    const walker = document.createTreeWalker(alert, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
        if (node.textContent.includes(label)) {
            return node;
        }
        node = walker.nextNode();
    }
    return null;
}

/**
 * Split `textNode` around the label and replace the label run with a styled
 * span, preserving any surrounding markup (e.g. the CMS `<strong>`).
 *
 * @param {Text} textNode - The text node containing the label.
 * @param {number} idx - The label's start index within the node.
 * @param {string} label - The label text.
 * @returns {void}
 */
function wrapLabel(textNode, idx, label) {
    const labelNode = idx === 0 ? textNode : textNode.splitText(idx);
    labelNode.splitText(label.length);

    const span = document.createElement("span");
    span.className = LABEL_CLASS;
    span.textContent = labelNode.textContent;
    labelNode.replaceWith(span);
}
